import PropTypes from "prop-types";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import Card from "@mui/material/Card";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CancelIcon from "@mui/icons-material/Cancel";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import RoomIcon from "@mui/icons-material/Room";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  useMaterialUIController,
  setActivePage,
  setConferenceData,
  setDialBroadcast,
  setActionButton,
} from "context";
import { useEffect, useState } from "react";
import CalendarRoot from "./CalendarRoot";
import PersonIcon from "@mui/icons-material/Person";
import TopicIcon from "@mui/icons-material/Topic";
import VideocamIcon from "@mui/icons-material/Videocam";
import CallIcon from "@mui/icons-material/Call";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
  List,
  ListItemText,
  Stack,
  Typography,
  Switch,
  styled,
  Checkbox,
  ListItemButton,
  Tooltip,
} from "@mui/material";
import CTLDialogContent from "application/components/styles/CTLDialogContent";
import MDButton from "components/MDButton";
import CTLConferenceDelete from "application/components/CTLConferenceDelete";
import { application } from "globals/endpoints";
import CTLNotification from "application/components/CTLNotification";
import CTLConferenceCancel from "application/components/CTLConferenceCancel";
import AuthenticationService from "application/modules/authentication/AuthenticationService";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import ConferenceList from "../ConferenceList";
import PlayDistorsion from "application/components/playDistorsion";
import CTLConferenceUserList from "application/components/CTLConferenceUserList";
import NextEvents from "../NextEvents";
import loggerFactory from "globals/logger/logger-factory";
import { Call, Campaign, Tune, Videocam } from "@mui/icons-material";
import MDBadge from "components/MDBadge";
import PasswordIcon from "@mui/icons-material/Password";
import AccessibilityIcon from "@mui/icons-material/Accessibility";
import ContactlessIcon from "@mui/icons-material/Contactless";
import CircleIcon from "@mui/icons-material/Circle";
import PhoneIcon from "@mui/icons-material/Phone";
import DialpadIcon from "@mui/icons-material/Dialpad";

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 50,
  height: 24,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(22px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(22px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: theme.palette.mode === "dark" ? "#177ddc" : "#1890ff",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,.35)"
        : "rgba(0,0,0,.25)",
    boxSizing: "border-box",
  },
}));

function Calendar({
  setOpen,
  setSelectedStartDate,
  setSelectedEndDate,
  setApiFormData,
  setCalendarKey,
  publicModeCount,
  localModeCount,
  calendarKey,
  openPopup,
  setConferenceMode,
  setOpenPopup,
  data,
  setSchedule,
  operationURLS,
  header,
  ...rest
}) {
  let click_To_Call = !!window.clickToCall;
  const currentDate = new Date();
  const [controller, dispatch] = useMaterialUIController();
  const { darkMode } = controller;
  const [type, setType] = useState("dayGridMonth");
  const [eventData, setEventData] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [phoneType, setPhoneType] = useState("");
  const [conferenceType, setConferenceType] = useState(false);
  const [calendarType, setCalendarType] = useState("month");
  const [participantList, setParticipantList] = useState(null);
  const [disable, setDisable] = useState(false);
  const [openDistorsion, setOpenDistorsion] = useState(false);

  const [timerLabel, setTimerLabel] = useState("");
  const [timerValue, setTimerValue] = useState("");

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  useEffect(() => {
    setCalendarKey(Date.now());
  }, [darkMode]);

  useEffect(() => {
    if (!openPopup || !eventData) return;

    const getDateTime = (dateStr, timeStr) => {
      const [h, m] = timeStr.split(":").map(Number);
      const d = new Date(dateStr);
      d.setHours(h, m, 0, 0);
      return d;
    };

    const updateTimer = () => {
      if (
        !eventData?.meetingScheduledDate ||
        !eventData?.fromTime ||
        !eventData?.toTime
      )
        return;

      const now = new Date();
      const start = getDateTime(
        eventData.meetingScheduledDate,
        eventData.fromTime
      );
      const end = getDateTime(
        eventData.scheduledToDate || eventData.meetingScheduledDate,
        eventData.toTime
      );

      if (now < start) {
        setTimerLabel("Conference starts in:");
        setTimerValue(formatDuration(start - now));
        setDisable(true);
      } else if (now >= start && now <= end) {
        setTimerLabel("Conference ends in:");
        setTimerValue(formatDuration(end - now));
        setDisable(false);
      } else if (now > end) {
        setTimerLabel("Conference Ended");
        setTimerValue("");
        setDisable(true);
      }
    };

    const formatDuration = (ms) => {
      const totalSeconds = Math.max(0, Math.floor(ms / 1000));
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [openPopup, eventData]);

  const classNames = [
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ];

  const events = rest.events
    ? rest.events.map((el) => ({
        ...el,
        className: classNames.find((item) => item === el.className)
          ? `event-${el.className}`
          : "event-info",
      }))
    : [];

  const headerToolbar = {
    left: "today prev,next",
    center: "title",
    right: "dayButton,weekButton,monthButton",
  };

  const customButtons = {
    // syncButton: {
    //   text: "↻",
    //   click: () => {
    //     setCalendarKey(Date.now());
    //   },
    // },
    weekButton: {
      text: "Week",
      click: () => {
        setType("timeGridWeek");
        setCalendarType("week");
        setCalendarKey(Date.now());
      },
    },
    dayButton: {
      text: "Day",
      click: () => {
        setType("timeGridDay");
        setCalendarType("day");
        setCalendarKey(Date.now());
      },
    },
    monthButton: {
      text: "Month",
      click: () => {
        setType("dayGridMonth");
        setCalendarType("month");
        setCalendarKey(Date.now());
      },
    },
  };

  const handleDateClick = (info) => {
    const clickedDate = info.date;
    if (calendarType == "month") {
      clickedDate.setHours(currentDate.getHours());
      clickedDate.setMinutes(currentDate.getMinutes() + 1);
    }
    if (clickedDate >= currentDate) {
      setOpen(true);
      setSelectedStartDate(clickedDate);
    } else {
      setNotify({
        isOpen: true,
        message: "",
        type: "info",
        pagename: "Conference",
        status: "Cannot create a conference with a past date.",
      });
    }
  };

  const handleDateSelect = (info) => {
    const StartDate = info.start;
    const EndDate = info.end;
    setSelectedDate(StartDate);
    if (StartDate >= currentDate) {
      setSelectedStartDate(StartDate);
      setSelectedEndDate(EndDate);
      setOpen(true);
    }
  };

  const handleDateUnselect = () => {
    setSelectedDate(null);
    setSelectedStartDate(null);
    setSelectedEndDate(null);
  };

  const handleCancel = () => {
    setConfirmCancel(true);
  };

  const handleAgreeCancel = () => {
    const body = {
      modId: eventData.modId,
    };
    application
      .post(operationURLS.cancel, body)
      .then((response) => {
        setCalendarKey(Date.now());
        setNotify({
          isOpen: true,
          message: response.data.status,
          type: "success",
          pagename: "Conference",
          status: response.data.messageDetail,
        });
      })
      .catch((error) => {
        setCalendarKey(Date.now());
        setNotify({
          isOpen: true,
          message: error.response.data.message,
          type: "error",
          pagename: "Conference",
          status: error.response.data.status,
        });
      });
    setCalendarKey(Date.now());
    setConfirmCancel(false);
    setOpenPopup(false);
  };

  const onCancel = () => {
    setConfirmCancel(false);
    setConfirmDelete(false);
  };

  const handleUpdate = () => {
    setOpen(true);
    setApiFormData(eventData);
    setOpenPopup(false);
  };

  const handleSchedule = () => {
    setOpen(true);
    setSchedule(true);
    setApiFormData(eventData);
    setOpenPopup(false);
  };
  const [activeUser, setActiveUser] = useState(null);
  const [conferenceDetails, setConferenceDetails] = useState(null);
  const fetchActiveData = (data) => {
    if (data) {
      application
        .post(`conferenceParticipant/confCallDetail/${data.accessCode}`)
        .then((res) => {
          if (res.data && res.data.data) {
            setActiveUser(res.data.data);
          } else {
            setActiveUser(null);
          }
        })
        .catch(() => {
          setActiveUser(null);
          loggerFactory.debug("Unable to load active users");
        });
    } else {
      loggerFactory.debug(
        "Data is not available, fetchActiveData was not called"
      );
    }
  };

  useEffect(() => {
    if (conferenceDetails) {
      const interval = setInterval(() => {
        fetchActiveData(conferenceDetails);
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [conferenceDetails]);

  const getColorForContact = (contactNumber) => {
    if (activeUser) {
      const activeContacts = activeUser.map((user) => user.contact);
      if (
        activeContacts.includes(
          contactNumber.extensions || contactNumber.mobileNumber
        )
      ) {
        return "success";
      }
    }
    return "error";
  };

  const handleEventClick = (info) => {
    const json = JSON.parse(info?.event?._def?.publicId);
    fetchActiveData(json);
    setConferenceDetails(json);
    setEventData(json);
    setOpenPopup(true);
    fetchData(json?.modId);
    if (info.event.end < currentDate || info.event.start > currentDate) {
      setDisable(true);
    } else {
      setDisable(false);
    }
  };

  const handleUserEventsClick = (item) => {
    setEventData(item);
    fetchData(item?.modId);

    const scheduledToDate = new Date(item.scheduledToDate);
    const scheduledFromDate = new Date(item.meetingScheduledDate);

    const timePartsTo = item.toTime.split(":");
    const timePartsFrom = item.fromTime.split(":");

    scheduledToDate.setHours(parseInt(timePartsTo[0], 10));
    scheduledToDate.setMinutes(parseInt(timePartsTo[1], 10));
    scheduledFromDate.setHours(parseInt(timePartsFrom[0], 10));
    scheduledFromDate.setMinutes(parseInt(timePartsFrom[1], 10));

    if (scheduledToDate < currentDate || scheduledFromDate > currentDate) {
      setNotify({
        isOpen: true,
        message: "",
        type: "info",
        pagename: "Conference",
        status:
          "The meeting has not started yet.Please wait until the meeting start",
      });
    } else {
      setConferenceData(dispatch, item);
      if (item.createdBy == AuthenticationService.getUserName()) {
        setPhoneType("moderator");
        setConferenceType();
      } else {
        setPhoneType("participant");
        setConferenceType();
      }
    }
  };

  const fetchData = async (modId) => {
    try {
      const response = await application.post(
        `conferenceParticipant/list/${modId}`
      );
      if (response.data) {
        setParticipantList(response.data.data);
      }
    } catch (error) {
      loggerFactory.debug("Failed to load Participant list-", error);
    }
  };

  const handleEventLeave = () => {
    setOpenPopup(false);
    setParticipantList(null);
  };

  const handleDelete = () => {
    setConfirmDelete(true);
  };

  const onAgree = (data) => {
    application
      .post(operationURLS.delete, {
        intId: [data.id],
      })
      .then((response) => {
        setOpenPopup(false);
        setCalendarKey(Date.now());
        setNotify({
          isOpen: true,
          message: response.data.messageDetail,
          type: "success",
          pagename: "Conference",
          status: response.data.message,
        });
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          message: error.response.data.status,
          type: "error",
          pagename: "Conference",
          status: error.response.data.message,
        });
      });
    setOpenPopup(false);
    setConfirmDelete(false);
    setCalendarKey(Date.now());
  };

  const convertTimeTo12HourFormat = (timeString) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":").map(Number);
    const period = hours >= 12 ? "pm" : "am";
    const hours12 = hours % 12 || 12;
    const formattedTime = `${hours12.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")} ${period}`;
    return formattedTime;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: "2-digit", month: "long", year: "numeric" };
    return date.toLocaleDateString("en-GB", options);
  };

  const handleEventDrop = (info) => {
    const meetingDetails = JSON.parse(info?.event?._def?.publicId);
    const dropEventStartTime = new Date(info.event.start);
    if (dropEventStartTime) {
      if (meetingDetails.createdBy == AuthenticationService.getUserName()) {
        var DropStartDate = new Date(info?.event?.start);
        var DropEndDate = new Date(info?.event?.end);

        var formattedDate = DropStartDate.toLocaleDateString("en-CA", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });

        var formattedToDate = DropEndDate.toLocaleDateString("en-CA", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });

        const adjustedFromHour =
          DropStartDate.getHours() === 24 ? "00" : DropStartDate.getHours();

        const formattedFromTime = `${adjustedFromHour
          .toString()
          .padStart(2, "0")}:${DropStartDate.getMinutes()
          .toString()
          .padStart(2, "0")}`;

        const adjustedToHour =
          DropStartDate.getHours() === 24 ? "00" : DropEndDate.getHours();

        const formattedToTime = `${adjustedToHour
          .toString()
          .padStart(2, "0")}:${DropEndDate.getMinutes()
          .toString()
          .padStart(2, "0")}`;

        meetingDetails.meetingScheduledDate = formattedDate;
        meetingDetails.scheduledToDate = formattedToDate;
        meetingDetails.fromTime = formattedFromTime;
        meetingDetails.toTime = formattedToTime;
        application
          .post(operationURLS.update, meetingDetails)
          .then((response) => {
            setCalendarKey(Date.now());
            setNotify({
              isOpen: true,
              message: response?.data?.messageDetail,
              type: "success",
              pagename: "Conference Event",
              status: response?.data?.message,
            });
          })
          .catch((error) => {
            setCalendarKey(Date.now());
            setNotify({
              isOpen: true,
              message: error.response?.data?.messageDetail,
              type: "error",
              pagename: "Conference Event",
              status: error.response?.data?.message,
            });
          });
      } else {
        setCalendarKey(Date.now());
        setNotify({
          isOpen: true,
          message: "",
          type: "info",
          pagename: "Conference Event",
          status: `This conference Created By ${meetingDetails.createdBy}, You can't modify`,
        });
      }
    } else {
      setCalendarKey(Date.now());
      setNotify({
        isOpen: true,
        message: "",
        type: "info",
        pagename: "Conference Event",
        status: `This conference can't drag on past day`,
      });
    }
  };

  const isWebView21 = () => {
    return (
      /wv|WebView|Edge\/\d+/i.test(navigator.userAgent) ||
      window.isWebView21 === true
    );
  };

  const joinConference = (data, type) => {
    if (isWebView21()) {
      if (getActiveStatus(AuthenticationService.getExtensionNumber())) {
        setNotify({
          isOpen: true,
          message: "",
          type: "warning",
          pagename: "Conference Meeting",
          status: `You are already in conference`,
        });
        return;
      }
      const message = JSON.stringify({
        eventType: "Conference Call",
        data: {
          Number: data.didMapping,
          accessCode: data.accessCode,
          isVideoCall: type,
        },
      });
      window?.chrome?.webview?.postMessage(message);
      loggerFactory.debug("conference event data send : ", message);
    } else {
      setConferenceData(dispatch, data);
      handleEventLeave();
      setPhoneType("participant");
      setConferenceType(type);
    }
  };

  const startConference = (data, type) => {
    if (isWebView21()) {
      if (getActiveStatus(AuthenticationService.getExtensionNumber())) {
        setNotify({
          isOpen: true,
          message: "",
          type: "warning",
          pagename: "Conference Meeting",
          status: `You are already in conference`,
        });
        return;
      }
      const message = JSON.stringify({
        eventType: "Conference Call",
        data: {
          Number: data.didMapping,
          accessCode: data.accessCode,
          isVideoCall: type,
        },
      });
      window?.chrome?.webview?.postMessage(message);
      loggerFactory.debug("conference event data send : ", message);
    } else {
      setConferenceData(dispatch, data);
      handleEventLeave();
      setPhoneType("moderator");
      setConferenceType(type);
    }
  };

  function HandleOpenDistorsion() {
    setOpenDistorsion(true);
  }

  function HandleCloseDistorsion() {
    setOpenDistorsion(false);
  }

  const handleChangeMode = (e) => {
    setConferenceMode(e.target.checked);
  };

  const [openBroadcast, setOpenBroadcast] = useState(false);
  const [ActiveConferenceList, setActiveConferenceList] = useState([]);
  const [selectedConferences, setSelectedConferences] = useState([]);

  const handleOpenBroadcastList = () => {
    application
      .post("conferenceRoom/active/list")
      .then((response) => {
        setActiveConferenceList(response.data.data);
        setOpenBroadcast(true);
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          message: error.response.data.status,
          type: "error",
          pagename: "Conference",
          status: error.response.data.message,
        });
      });
  };

  const handleBroadcastLeave = () => {
    setOpenBroadcast(false);
    setSelectedConferences([]);
  };

  const handleMulticastStart = () => {
    setActionButton(dispatch, "Multicast");
    setPhoneType("moderator");
    setDialBroadcast(dispatch, selectedConferences);
    loggerFactory.info(
      "selected conference for broadcast ",
      selectedConferences
    );
    setOpenBroadcast(false);
    setSelectedConferences([]);
  };

  const handleToggle = (item) => {
    const currentIndex = selectedConferences.indexOf(item);
    const newSelectedConferences = [...selectedConferences];
    if (currentIndex === -1) {
      newSelectedConferences.push(item);
    } else {
      newSelectedConferences.splice(currentIndex, 1);
    }
    setSelectedConferences(newSelectedConferences);
  };

  const handleBroadcast = () => {
    application
      .post("conferenceRoom/active/list")
      .then((response) => {
        const data = response.data.data;
        if (data.length !== 0) {
          setActionButton(dispatch, "Broadcast");
          setPhoneType("moderator");
          setDialBroadcast(dispatch, data);
        }

        setNotify({
          isOpen: true,
          message: "",
          type: "success",
          pagename: "Conference",
          status: response.data.message,
        });
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          message: error.response.data.status,
          type: "error",
          pagename: "Conference",
          status: error.response.data.message,
        });
      });
  };

  const getActiveStatus = (contactNumber) => {
    if (activeUser) {
      const matchedContact = activeUser.find(
        (item) => item.contact === contactNumber
      );
      if (matchedContact) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  const handleMuteAll = () => {
    application
      .post(`conferenceParticipant/mute/all/${eventData.accessCode}`)
      .then((response) => {
        setNotify({
          isOpen: true,
          message: "",
          type: "success",
          pagename: "Conference",
          status: response.data.message,
        });
      })
      .catch((error) => {
        loggerFactory.debug("Unable to Mute participant :{} ", error);
      });
  };

  const handleUnMuteAll = () => {
    application
      .post(`conferenceParticipant/unmute/all/${eventData.accessCode}`)
      .then((response) => {
        setNotify({
          isOpen: true,
          message: "",
          type: "success",
          pagename: "Conference",
          status: response.data.message,
        });
      })
      .catch((error) => {
        loggerFactory.debug("Unable to UnMute participant :{} ", error);
      });
  };

  const call = (data) => {
    if (getActiveStatus(AuthenticationService.getExtensionNumber())) {
      setNotify({
        isOpen: true,
        message: "",
        type: "warning",
        pagename: "Conference Meeting",
        status: `You are already in conference`,
      });
      return;
    }
    const isModerator =
      data.createdBy == AuthenticationService.getUserName() ? true : false;
    const updatedConferenceData = {
      ...data,
      conferenceNumber: data.didMapping,
    };
    const participantNumbers = Array.isArray(data.extensions)
      ? [...data.extensions]
      : [data.extensions];
    const updatedData = {
      conferenceId: data.accessCode,
      participantNumber: participantNumbers,
      conferenceNumber: updatedConferenceData.conferenceNumber,
      isModerator,
    };
    application
      .post(`conferenceParticipant/dialConference`, updatedData)
      .then((response) => {
        setNotify({
          isOpen: true,
          message: "",
          type: "success",
          pagename: "Conference",
          status: response.data.message,
        });
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          message: error.response.data.message,
          type: "error",
          pagename: "Conference",
          status: error.response.data.status,
        });
      });
  };

  const handleMute = (number) => {
    const matchedContact =
      activeUser && activeUser.find((item) => item.contact === number);
    if (matchedContact) {
      application
        .post(
          `conferenceParticipant/mute/${eventData?.accessCode}/${matchedContact.id}`
        )
        .then((response) => {
          fetchData(eventData?.modId);
          setNotify({
            isOpen: true,
            message: "",
            type: "success",
            pagename: "Conference",
            status: response.data.message,
          });
        })
        .catch((error) => {
          loggerFactory.debug("Unable to Mute participant ", error);
        });
    } else {
      setNotify({
        isOpen: true,
        message: "",
        type: "warning",
        pagename: "Conference",
        status: `Unable to Mute participant no match user found !`,
      });
    }
  };

  const handleUnMute = (number) => {
    const matchedContact =
      activeUser && activeUser.find((item) => item.contact === number);
    if (matchedContact) {
      application
        .post(
          `conferenceParticipant/unmute/${eventData.accessCode}/${matchedContact.id}`
        )
        .then((response) => {
          fetchData(eventData?.modId);
          setNotify({
            isOpen: true,
            message: "",
            type: "success",
            pagename: "Conference",
            status: response.data.message,
          });
        })
        .catch((error) => {
          loggerFactory.debug("Unable to UnMute participant ", error);
        });
    } else {
      setNotify({
        isOpen: true,
        message: "",
        type: "warning",
        pagename: "Conference",
        status: `Unable to UnMute participant no match user found ! `,
      });
    }
  };

  const getMuteStatus = (contactNumber) => {
    if (activeUser) {
      const matchedContact = activeUser.find(
        (item) => item.contact === contactNumber
      );
      if (matchedContact && matchedContact.canSpeak === "false") {
        return false;
      } else {
        return true;
      }
    }
  };

  const handleDial = (data) => {
    if (activeUser) {
      setNotify({
        isOpen: true,
        message: "Conference is already started",
        type: "error",
        pagename: "Conference",
        status: "Already Started",
      });
      return;
    }
    const participantExtensions = participantList.map(
      (participant) => participant.extensions
    );
    const combinedExtensions = [...participantExtensions, data.extensions];
    const updatedConferenceData = {
      ...data,
      conferenceNumber: data.didMapping,
    };
    const updatedData = {
      conferenceId: data.accessCode,
      participantNumber: combinedExtensions,
      conferenceNumber: updatedConferenceData.conferenceNumber,
    };
    application
      .post("conferenceParticipant/dialConference", updatedData)
      .then((response) => {
        setNotify({
          isOpen: true,
          message: "",
          type: "success",
          pagename: "Conference",
          status: response.data.message,
        });
      })
      .catch((error) => {
        setNotify({
          isOpen: true,
          message: error.response.data.message,
          type: "error",
          pagename: "Conference",
          status: error.response.data.status,
        });
      });
  };


  const highlightActiveButton = (active) => {
    const buttons = {
      week: document.querySelector(".fc-weekButton-button"),
      day: document.querySelector(".fc-dayButton-button"),
      month: document.querySelector(".fc-monthButton-button"),
    };

    Object.keys(buttons).forEach((key) => {
      if (buttons[key]) {
        if (key === active) {
          buttons[key].style.background = "#1976d2";
          buttons[key].style.color = "#fff";
          buttons[key].style.border = "1px solid #1976d2";
        } else {
          buttons[key].style.background = "#fff";
          buttons[key].style.color = "#000";
          buttons[key].style.border = "1px solid #ccc";
        }
      }
    });
  };

  return (
    <>
      {eventData && participantList && (
        <Dialog
          open={openPopup}
          onClose={handleEventLeave}
          fullWidth
          fullScreen
        >
          <DialogTitle sx={(theme) => CTLDialogContent(theme, { darkMode })}>
            <Grid container spacing={1} direction="row">
              <Grid
                item
                xs={12}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "24px",
                  width: "100%",
                }}
              >
                <MDTypography
                  variant="h4"
                  sx={{ flex: 2, textAlign: "left", fontWeight: "bold" }}
                >
                  Meeting Details
                </MDTypography>

                {timerLabel && (
                  <Box
                    sx={{
                      flex: 1,
                      textAlign: "center",
                      p: 0,
                      borderRadius: 3,
                      boxShadow: 3,
                      minWidth: "220px",
                      mx: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        timerLabel === "Conference starts in:"
                          ? "linear-gradient(90deg, #1976d2 0%, #005eacff 100%)"
                          : timerLabel === "Conference ends in:"
                          ? "linear-gradient(90deg, #388e3c 0%, #099c10ff 100%)"
                          : "linear-gradient(90deg, #d32f2f 0%, #ad250dff 100%)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: "bold",
                        color: "#fff",
                        letterSpacing: 2,
                        textShadow: "1px 1px 8px rgba(0,0,0,0.15)",
                      }}
                    >
                      {timerLabel} {timerValue}
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    flex: 2,
                    display: "flex",
                    gap: 1,
                    justifyContent: "flex-end",
                  }}
                >
                  {eventData?.createdBy ==
                    AuthenticationService.getUserName() && (
                    <>
                      <tooltip title="Edit Conference">
                        <MDButton
                          color="info"
                          style={{ borderRadius: "20%", padding: "0px" }}
                          onClick={handleUpdate}
                        >
                          <EditIcon style={{ width: "25px", height: "25px" }} />
                        </MDButton>
                      </tooltip>
                      <tooltip title="Re-Schedule Conference">
                        <MDButton
                          color="success"
                          style={{ borderRadius: "20%", padding: "0px" }}
                          onClick={handleSchedule}
                        >
                          <ScheduleIcon
                            style={{ width: "25px", height: "25px" }}
                          />
                        </MDButton>
                      </tooltip>
                      <tooltip title="Cancel Conference">
                        <MDButton
                          color="warning"
                          style={{ borderRadius: "20%", padding: "0px" }}
                          onClick={handleCancel}
                        >
                          <CancelIcon
                            style={{ width: "25px", height: "25px" }}
                          />
                        </MDButton>
                      </tooltip>
                      <tooltip title="Delete Conference">
                        <MDButton
                          color="error"
                          style={{ borderRadius: "20%", padding: "0px" }}
                          onClick={handleDelete}
                        >
                          <DeleteIcon
                            style={{ width: "25px", height: "25px" }}
                          />
                        </MDButton>
                      </tooltip>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent sx={(theme) => CTLDialogContent(theme, { darkMode })}>
            <Box
              sx={{
                border: (theme) =>
                  `1px solid ${
                    darkMode
                      ? theme.palette.common.white
                      : theme.palette.common.black
                  }`,
                borderRadius: 2,
                padding: 1,
                marginBottom: 3,
              }}
            >
              <Grid container spacing={2}>
                {/* Column 1 */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{ fontSize: 20, display: "flex", alignItems: "center" }}
                  >
                    <TopicIcon sx={{ mr: 1 }} color="info" />
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      Meeting Name:
                    </Box>
                    &nbsp;{eventData?.roomName}
                  </Typography>

                  <Typography
                    sx={{ fontSize: 20, display: "flex", alignItems: "center" }}
                  >
                    <CalendarTodayIcon sx={{ mr: 1 }} color="info" />
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      Meeting Date:
                    </Box>
                    &nbsp;
                    {eventData?.meetingScheduledDate &&
                    eventData?.scheduledToDate &&
                    formatDate(eventData?.meetingScheduledDate) !==
                      formatDate(eventData?.scheduledToDate)
                      ? `${formatDate(
                          eventData.meetingScheduledDate
                        )} - ${formatDate(eventData.scheduledToDate)}`
                      : formatDate(eventData?.meetingScheduledDate)}
                  </Typography>

                  <Typography
                    sx={{ fontSize: 20, display: "flex", alignItems: "center" }}
                  >
                    <AccessTimeIcon sx={{ mr: 1 }} color="info" />
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      Meeting Time:
                    </Box>
                    &nbsp;{convertTimeTo12HourFormat(eventData?.fromTime)} -{" "}
                    {convertTimeTo12HourFormat(eventData?.toTime)}
                  </Typography>

                  <Typography
                    sx={{ fontSize: 20, display: "flex", alignItems: "center" }}
                  >
                    <RadioButtonCheckedIcon
                      sx={{ mr: 1 }}
                      color={eventData?.recording ? "success" : "error"}
                    />
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      Recording:
                    </Box>
                    &nbsp;{eventData?.recording ? "On" : "Off"}
                  </Typography>
                  {AuthenticationService.allowUCMenus() && (
                    <Typography
                      sx={{
                        fontSize: 20,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <TopicIcon sx={{ mr: 1 }} color="info" />
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        Meeting Type:
                      </Box>
                      &nbsp;{eventData?.meetingType}
                    </Typography>
                  )}
                </Grid>

                {/* Column 2 */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{ fontSize: 20, display: "flex", alignItems: "center" }}
                  >
                    <AccountCircleIcon sx={{ mr: 1 }} color="info" />
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      Meeting Created By:
                    </Box>
                    &nbsp;
                    {eventData?.createdBy ===
                    AuthenticationService.getUserName()
                      ? "You"
                      : eventData?.createdBy}
                  </Typography>

                  <Typography
                    sx={{ fontSize: 20, display: "flex", alignItems: "center" }}
                  >
                    <ContactlessIcon sx={{ mr: 1 }} color="info" />
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      Conference Number:
                    </Box>
                    &nbsp;{eventData?.didMapping}
                  </Typography>

                  <Typography
                    sx={{ fontSize: 20, display: "flex", alignItems: "center" }}
                  >
                    <AccessibilityIcon sx={{ mr: 1 }} color="info" />
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      Access Code:
                    </Box>
                    &nbsp;{eventData?.accessCode}
                  </Typography>

                  <Typography
                    sx={{ fontSize: 20, display: "flex", alignItems: "center" }}
                  >
                    {eventData?.conferenceType === "v" ? (
                      <VideocamIcon sx={{ mr: 1 }} color="info" />
                    ) : (
                      <CallIcon sx={{ mr: 1 }} color="info" />
                    )}
                    <Box component="span" sx={{ fontWeight: "bold" }}>
                      Conference Mode:
                    </Box>
                    &nbsp;
                    {eventData?.conferenceType === "v" ? "Video" : "Audio"}
                  </Typography>
                  {eventData?.passwordRequired && (
                    <Typography
                      sx={{
                        fontSize: 20,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <LockOpenIcon sx={{ mr: 1 }} color="error" />
                      <Box component="span" sx={{ fontWeight: "bold" }}>
                        Password:
                      </Box>
                      &nbsp;{eventData?.conferencePassword}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>

            {AuthenticationService.allowConferenceMenus() ||
            AuthenticationService.allowUCMenus() ? (
              <CTLConferenceUserList
                conferenceData={eventData}
                phoneType={
                  eventData?.createdBy == AuthenticationService.getUserName()
                    ? "moderator"
                    : "participant"
                }
                openPopup={openPopup}
              />
            ) : null}
          </DialogContent>
          <DialogActions sx={(theme) => CTLDialogContent(theme, { darkMode })}>
            <MDButton color="secondary" onClick={handleEventLeave}>
              Close
            </MDButton>

            {getColorForContact(eventData) === "success" &&
              eventData?.createdBy === AuthenticationService.getUserName() &&
              (getMuteStatus(eventData.extensions) ? (
                <Tooltip title="Mute Yourself">
                  <MDButton
                    variant="contained"
                    color="success"
                    aria-label="Unmute"
                    onClick={() =>
                      handleMute(eventData.extensions || eventData.mobileNumber)
                    }
                  >
                    <Icon>mic</Icon>
                  </MDButton>
                </Tooltip>
              ) : (
                <Tooltip title="Unmute Yourself">
                  <MDButton
                    variant="contained"
                    color="error"
                    aria-label="Mute"
                    onClick={() =>
                      handleUnMute(
                        eventData.extensions || eventData.mobileNumber
                      )
                    }
                  >
                    <Icon>mic_off</Icon>
                  </MDButton>
                </Tooltip>
              ))}

            {eventData?.createdBy == AuthenticationService.getUserName() && (
              <>
                <MDButton
                  disabled={disable}
                  color="error"
                  onClick={handleMuteAll}
                  startIcon={<Icon>mic</Icon>}
                >
                  Mute All
                </MDButton>
                <MDButton
                  disabled={disable}
                  color="success"
                  onClick={handleUnMuteAll}
                  startIcon={<Icon>mic_off</Icon>}
                >
                  Unmute All
                </MDButton>
              </>
            )}
            {eventData?.createdBy == AuthenticationService.getUserName() &&
              !click_To_Call &&
              conferenceMode && (
                <>
                  <MDButton
                    color="warning"
                    variant="contained"
                    id="call"
                    size="medium"
                    disabled={disable}
                    onClick={() => HandleOpenDistorsion()}
                  >
                    <Icon color="text">campaign_icon</Icon> &nbsp; Play
                    Announcement
                  </MDButton>
                </>
              )}

            {AuthenticationService.allowConferenceMenus() &&
              (isWebView21() || !click_To_Call) && (
                <>
                  <MDButton
                    color={
                      eventData?.createdBy ==
                      AuthenticationService.getUserName()
                        ? "success"
                        : "info"
                    }
                    disabled={disable}
                    onClick={() => {
                      eventData?.createdBy ==
                      AuthenticationService.getUserName()
                        ? startConference(eventData, false)
                        : joinConference(eventData, false);
                    }}
                  >
                    {eventData?.createdBy == AuthenticationService.getUserName()
                      ? "Join Conference"
                      : "Join Conference"}
                  </MDButton>
                </>
              )}

            {AuthenticationService.allowUCMenus() && isWebView21() && (
              <>
                <MDButton
                  color={"info"}
                  disabled={disable}
                  onClick={() => {
                    eventData?.createdBy == AuthenticationService.getUserName()
                      ? startConference(eventData, false)
                      : joinConference(eventData, false);
                  }}
                >
                  <Icon>call_icon</Icon> &nbsp; Join Audio Meeting
                </MDButton>
                {eventData.meetingType === "Meet Me Conference" &&
                  eventData?.conferenceType === "v" && (
                    <MDButton
                      color={"info"}
                      disabled={disable}
                      onClick={() => {
                        eventData?.createdBy ==
                        AuthenticationService.getUserName()
                          ? startConference(eventData, true)
                          : joinConference(eventData, true);
                      }}
                    >
                      <Icon>videocam_icon</Icon>&nbsp; Join Video Meeting
                    </MDButton>
                  )}
              </>
            )}
            {!isWebView21() &&
              click_To_Call &&
              eventData?.createdBy == AuthenticationService.getUserName() && (
                <MDButton
                  color="info"
                  disabled={disable}
                  onClick={() => handleDial(eventData)}
                >
                  <PhoneIcon size="large" color="text" />
                  &nbsp; Dial All Participants
                </MDButton>
              )}
            {click_To_Call && !isWebView21() && (
              <>
                <MDButton
                  color={getColorForContact(eventData)}
                  onClick={() => call(eventData)}
                  disabled={disable}
                >
                  <PhoneIcon color="text" size="large" />
                  &nbsp; Join Audio Meeting
                </MDButton>
              </>
            )}
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={openBroadcast} onClose={handleBroadcastLeave} fullWidth>
        <DialogTitle sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          Select Conference To Multicast
        </DialogTitle>
        <DialogContent sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          {ActiveConferenceList.length == 0 ? (
            <Box>
              <List>
                <ListItemText
                  sx={{ marginLeft: 2 }}
                  primary="No any active conference"
                />
              </List>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 230, overflow: "auto" }}>
              <List>
                {ActiveConferenceList.map((item) => {
                  const labelId = `checkbox-list-label-${item}`;
                  return (
                    <ListItemButton
                      key={item.uuid}
                      onClick={() => handleToggle(item)}
                      alignItems="center"
                    >
                      <Checkbox
                        edge="end"
                        checked={selectedConferences.indexOf(item) !== -1}
                        onChange={() => handleToggle(item)}
                        inputProps={{ "aria-labelledby": labelId }}
                      />
                      <ListItemText
                        id={labelId}
                        primary={`${item.conferenceName}`}
                        sx={{ marginLeft: 2 }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          <MDButton color="secondary" onClick={handleBroadcastLeave}>
            Close
          </MDButton>
          <MDButton
            color="success"
            disabled={ActiveConferenceList.length == 0 ? true : false}
            onClick={handleMulticastStart}
          >
            Start
          </MDButton>
        </DialogActions>
      </Dialog>

      <Grid container spacing={1}>
        <Grid
          item
          xs={!click_To_Call && !isWebView21() ? 9 : 12}
          sx={{ height: "max-content" }}
        >
          {" "}
          {AuthenticationService.allowConferenceMenus() ||
          AuthenticationService.allowUCMenus() ? (
            <Card
              style={{
                maxHeight: "calc(100vh - 150px)",
                minHeight: "calc(100vh - 150px)",
                overflow: "auto",
                position: "relative",
                width: "100%",
              }}
            >
              {AuthenticationService.allowConferenceMenus() &&
                window.conferenceMode && (
                  <>
                    <Grid container alignItems="center" justifyContent="center">
                      <Grid item={8}>
                        <Stack
                          padding="20px"
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="center"
                        >
                          <MDBadge
                            color="error"
                            badgeContent={localModeCount}
                            max={99}
                            size="xs"
                            circular
                          >
                            <MDTypography variant="h6">
                              {window.conferenceMode1
                                ? window.conferenceMode1
                                : "Local Mode"}
                            </MDTypography>
                          </MDBadge>
                          &nbsp;&nbsp;
                          <AntSwitch
                            onChange={(e) => handleChangeMode(e)}
                            inputProps={{ "aria-label": "ant design" }}
                          />
                          <MDBadge
                            color="error"
                            badgeContent={publicModeCount}
                            max={99}
                            size="xs"
                            circular
                          >
                            <MDTypography variant="h6">
                              {window.conferenceMode2
                                ? window.conferenceMode2
                                : "Public Mode"}
                            </MDTypography>
                          </MDBadge>
                        </Stack>
                      </Grid>
                      <Grid item xs={4}>
                        {" "}
                        <IconButton
                          style={{ marginLeft: "50px" }}
                          onClick={() => handleBroadcast()}
                        >
                          <MDTypography variant="h6">Broadcast</MDTypography>{" "}
                          &nbsp;
                          <Campaign color="error"></Campaign>
                        </IconButton>
                        <IconButton
                          style={{ marginLeft: "30px" }}
                          onClick={() => handleOpenBroadcastList()}
                        >
                          <MDTypography variant="h6">Multicast</MDTypography>{" "}
                          &nbsp;
                          <Campaign color="warning"></Campaign>
                        </IconButton>
                      </Grid>
                    </Grid>
                  </>
                )}
              <Box
                sx={{
                  height: "calc(100vh - 64px)", 
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  p: 1,
                }}
              >
                <CalendarRoot p={2} ownerState={{ darkMode }}>
                  {type && (
                    <FullCalendar
                      {...rest}
                      initialDate={selectedDate || new Date()}
                      key={calendarKey}
                      plugins={[
                        dayGridPlugin,
                        timeGridPlugin,
                        interactionPlugin,
                      ]}
                      droppable={true}
                      eventDrop={handleEventDrop}
                      events={events}
                      eventClick={handleEventClick}
                      eventLeave={handleEventLeave}
                      initialView={type}
                      height="100%"
                      customButtons={customButtons}
                      headerToolbar={headerToolbar}
                      dateClick={handleDateClick}
                      select={handleDateSelect}
                      unselect={handleDateUnselect}
                      selectMirror={true}
                      nowIndicator={true}
                      selectOverlap={false}
                      selectMinDistance={2}
                      slotLabelFormat={{
                        hour: "numeric",
                        minute: "2-digit",
                        omitZeroMinute: false,
                        meridiem: "short",
                      }}
                      datesSet={(info) => {
                        highlightActiveButton(calendarType);
                      }}
                      slotLabelDidMount={(args) => {
                        const label = args.el;
                        label.style.color = darkMode ? "white" : "black";
                      }}
                    />
                  )}
                </CalendarRoot>
              </Box>
            </Card>
          ) : (
            <ConferenceList
              data={data}
              handleUserEventsClick={handleUserEventsClick}
              setConferenceMode={setConferenceMode}
            />
          )}
        </Grid>
        {!click_To_Call && !isWebView21() && (
          <Grid item xs={3}>
            <MDBox mb={3}>
              <NextEvents
                data={data}
                phoneType={phoneType}
                setPhoneType={setPhoneType}
                setConferenceType={setConferenceType}
                conferenceType={conferenceType}
                handleUserEventsClick={handleUserEventsClick}
              />
            </MDBox>
          </Grid>
        )}
      </Grid>

      <CTLConferenceDelete
        confirmDelete={confirmDelete}
        onAgree={onAgree}
        onCancel={onCancel}
        eventData={eventData}
      />
      <CTLConferenceCancel
        confirmCancel={confirmCancel}
        handleAgreeCancel={handleAgreeCancel}
        onCancel={onCancel}
        eventData={eventData}
      />

      <PlayDistorsion
        entityName="Play Distorsion"
        openDistorsion={openDistorsion}
        setOpenDistorsion={setOpenDistorsion}
        HandleCloseDistorsion={HandleCloseDistorsion}
        conferenceData={eventData}
      />
      <CTLNotification notify={notify} setNotify={setNotify} />
    </>
  );
}

Calendar.defaultProps = {
  header: {
    title: "",
    date: "",
  },
};

Calendar.propTypes = {
  header: PropTypes.shape({
    title: PropTypes.string,
    date: PropTypes.string,
  }),
};

export default Calendar;
